import React from 'react'
import PropTypes from 'prop-types'
// import { Helmet as ReactHelmet } from 'react-helmet-async'
import { Helmet as ReactHelmet } from 'react-helmet-async'
import { useStaticQuery, graphql } from 'gatsby'
import { useImageSize } from 'react-image-size'

import nonIndexedPages from 'lib/gatsby/non-indexed-pages'

const DEFAULT_SOCIAL_IMAGE_WIDTH = 600
const DEFAULT_SOCIAL_IMAGE_HEIGHT = 315

// NOTE: this component is to isolate all head tag stuff, and enforce good
// SEO practices. any changes here, verify the meta tags on pages look right
// by putting $('title'), $('meta[name="description"]'), $('meta[name="keywords"]'),
// etc in the console of your browser
export default function Helmet ({
  children,
  pathname,
  canonicalPathname,
  title,
  meta: { description, socialTagline, keywords },
  // NOTE: passing this separate from meta prop...
  // and the image for socials will be static/not be something marketeres will want to
  // experiment with. THIS ALSO MUST BE AN ABSOLUTE PATH
  // TODO: extend this to take absolute paths AND relative paths. something like:
  // { absolutePath: '', relativePath: '' }
  socialImageURL,
  schema,
  metaArray, // for additional meta tags
}) {
  const {
    site: {
      siteMetadata: { siteUrl },
    },
  } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)

  // ensure leading slash
  pathname = pathname.startsWith('/') ? pathname : `/${pathname}`
  if (canonicalPathname) {
    canonicalPathname = canonicalPathname.startsWith('/')
      ? canonicalPathname
      : `/${canonicalPathname}`
  }

  const finalCanonicalURL = `${siteUrl}${canonicalPathname || pathname}`

  const [data] = useImageSize(socialImageURL)
  const socialImageWidth = data?.width
  const socialImageHeight = data?.height

  const isEmptyMetaObject = !(description && socialTagline && keywords)
  const useDefaultImgWidth = isEmptyMetaObject && !socialImageWidth
  const useDefaultImgHeight = isEmptyMetaObject && !socialImageHeight

  const meta = (
    <>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {(title || socialTagline) && (
        <meta name="twitter:title" content={socialTagline || title} />
      )}
      {description && <meta name="twitter:description" content={description} />}
      {(title || socialTagline) && (
        <meta property="og:title" content={socialTagline || title} />
      )}
      {description && <meta property="og:description" content={description} />}
      {socialImageURL && <meta name="twitter:image" content={socialImageURL} />}
      {socialImageURL && <meta property="og:image" content={socialImageURL} />}
      {socialImageURL && (
        <meta
          property="og:image:type"
          content={imageUrlToMimeType(socialImageURL)}
        />
      )}
      <meta property="og:url" content={finalCanonicalURL} />
      {useDefaultImgWidth && (
        <meta
          property="og:image:width"
          content={`${DEFAULT_SOCIAL_IMAGE_WIDTH}`}
        />
      )}
      {useDefaultImgHeight && (
        <meta
          property="og:image:height"
          content={`${DEFAULT_SOCIAL_IMAGE_HEIGHT}`}
        />
      )}
      {socialImageWidth && (
        <meta property="og:image:width" content={`${socialImageWidth}`} />
      )}
      {socialImageHeight && (
        <meta property="og:image:height" content={`${socialImageHeight}`} />
      )}
      {nonIndexedPages.includes(pathname) && (
        <meta name="robots" content="noindex" />
      )}
      {metaArray}
    </>
  )

  const canonicalLink = <link rel="canonical" href={finalCanonicalURL} />

  const helmetChildren = (
    <>
      {meta}
      {children}
      {canonicalLink}
      {schema ? (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      ) : null}
    </>
  )

  const helmetProps = title ? { title } : {}

  return <ReactHelmet {...helmetProps}>{helmetChildren}</ReactHelmet>
}

Helmet.propTypes = {
  pathname: PropTypes.string.isRequired,
  canonicalPathname: PropTypes.string,
  title: PropTypes.string,
  meta: PropTypes.shape({
    description: PropTypes.string,
    socialTagline: PropTypes.string,
    keywords: PropTypes.string,
  }),
  socialImageURL: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node)]),
  // json-ld schema from schema.org
  schema: PropTypes.shape({
    '@context': PropTypes.string.isRequired,
    '@type': PropTypes.string.isRequired,
    // others depend on the type
  }),
  // react helmet is supposed to automatically handle children overriding
  // meta tags, but it doesn't seem to be working. so we need to pass in this
  // array of meta tags to override the default ones for more specific helmets
  // like ArticleHelmet
  metaArray: PropTypes.array,
}

// TODO: move this to a helper file
function imageUrlToMimeType (imageUrl) {
  let ext = imageUrl.split?.('.')?.pop()
  // NOTE: this is a sanity case, where we serve our svg covers as png for socials
  // so we need to get the correct ext from the string
  if (ext.length > 4 && ext.includes('?fm=')) {
    ext = ext.split('?fm=')[1]
  }
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    case 'png':
      return 'image/png'
    // dunno if we support gif as a social cover...
    case 'gif':
      return 'image/gif'
    case 'svg':
      return 'image/svg+xml'
    case 'webp':
      return 'image/webp'
    default:
      return null
  }
}
